// Feedback & Suggestions — retailer-portal Settings → Feedback page.
//
// Steve 2026-05-22 — first iteration of the platform-wide Support feature.
// Foundation for the future AI bug-reporting system.
//
// Three fields: type (bug/feature/general), title, description. Optional
// screenshot. POSTs to `/api/portal/feedback` as multipart/form-data so the
// screenshot can ride along. Confirmation message replaces the form on
// success; a "Submit another" link restores the empty form.

import { registerPage } from "../../shell/router";
import { iconMarkup } from "@tkos/shared-ui";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

type FeedbackType = "bug" | "feature" | "general";

const TYPES: Array<{ id: FeedbackType; label: string; description: string }> = [
  { id: "bug",     label: "Bug Report",       description: "Something is broken or behaving unexpectedly" },
  { id: "feature", label: "Feature Request",  description: "An idea for something new the platform should do" },
  { id: "general", label: "General Feedback", description: "Anything else — comments, suggestions, observations" },
];

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const feedbackPage: Page = {
  name: "feedback",
  label: "Feedback & Suggestions",
  icon: "",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    container.classList.add("fb-page-root");
    container.innerHTML = `
      <section class="fb-page">
        <header class="fb-page__head">
          <h1>Feedback & Suggestions</h1>
          <p class="fb-page__sub">
            Report bugs, request features, or share general feedback.
            Anything you submit lands directly with the platform team.
          </p>
        </header>

        <div class="fb-card" data-role="form-card">
          <form class="fb-form" data-role="form" novalidate>
            <fieldset class="fb-field">
              <legend>Type</legend>
              <div class="fb-type-grid" role="radiogroup" aria-label="Feedback type">
                ${TYPES.map((t, i) => `
                  <button type="button"
                          class="fb-type-btn${i === 0 ? " is-active" : ""}"
                          data-type="${t.id}"
                          role="radio"
                          aria-checked="${i === 0 ? "true" : "false"}">
                    <span class="fb-type-btn__label">${esc(t.label)}</span>
                    <span class="fb-type-btn__sub">${esc(t.description)}</span>
                  </button>
                `).join("")}
              </div>
            </fieldset>

            <div class="fb-field">
              <label for="fb-title">Title</label>
              <input id="fb-title" name="title" type="text"
                     maxlength="200"
                     placeholder="Short summary of the issue or idea" required />
            </div>

            <div class="fb-field">
              <label for="fb-description">Description</label>
              <textarea id="fb-description" name="description"
                        rows="6" maxlength="8000"
                        placeholder="Tell us what happened, what you expected, and how to reproduce it (for bugs) — or describe the idea (for features)."
                        required></textarea>
            </div>

            <div class="fb-field">
              <label for="fb-screenshot">Screenshot <span class="fb-field__hint">(optional · PNG/JPG/WEBP · max 5 MB)</span></label>
              <input id="fb-screenshot" name="screenshot" type="file"
                     accept="image/png,image/jpeg,image/webp" />
              <div class="fb-screenshot-name" data-role="screenshot-name" hidden></div>
            </div>

            <div class="fb-form-footer">
              <span class="fb-form-error" data-role="error" hidden></span>
              <button type="submit" class="fb-btn fb-btn--primary" data-role="submit">
                ${iconMarkup("send", { size: "sm" })}
                <span data-role="submit-label">Submit feedback</span>
              </button>
            </div>
          </form>
        </div>

        <div class="fb-confirm" data-role="confirm-card" hidden>
          <div class="fb-confirm__icon">${iconMarkup("check", { size: "lg" })}</div>
          <h2>Thanks — we've got it.</h2>
          <p>Your feedback has landed with the platform team. We read everything; we'll come back to you if we need clarification.</p>
          <button type="button" class="fb-btn" data-role="reset">Submit another</button>
        </div>
      </section>
    `;

    const formCard   = container.querySelector<HTMLDivElement>("[data-role='form-card']")!;
    const form       = container.querySelector<HTMLFormElement>("[data-role='form']")!;
    const titleInput = container.querySelector<HTMLInputElement>("#fb-title")!;
    const descInput  = container.querySelector<HTMLTextAreaElement>("#fb-description")!;
    const fileInput  = container.querySelector<HTMLInputElement>("#fb-screenshot")!;
    const fileName   = container.querySelector<HTMLDivElement>("[data-role='screenshot-name']")!;
    const submitBtn  = container.querySelector<HTMLButtonElement>("[data-role='submit']")!;
    const submitLbl  = container.querySelector<HTMLSpanElement>("[data-role='submit-label']")!;
    const errorEl    = container.querySelector<HTMLSpanElement>("[data-role='error']")!;
    const confirmCard = container.querySelector<HTMLDivElement>("[data-role='confirm-card']")!;
    const resetBtn   = container.querySelector<HTMLButtonElement>("[data-role='reset']")!;
    const typeBtns   = Array.from(
      container.querySelectorAll<HTMLButtonElement>(".fb-type-btn"),
    );

    let selectedType: FeedbackType = "bug";
    let submitting = false;

    function setError(msg: string | null): void {
      if (!msg) {
        errorEl.hidden = true;
        errorEl.textContent = "";
        return;
      }
      errorEl.hidden = false;
      errorEl.textContent = msg;
    }

    function selectType(t: FeedbackType): void {
      selectedType = t;
      for (const btn of typeBtns) {
        const active = btn.dataset.type === t;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-checked", active ? "true" : "false");
      }
    }

    function showConfirm(): void {
      formCard.hidden = true;
      confirmCard.hidden = false;
    }

    function resetForm(): void {
      titleInput.value = "";
      descInput.value = "";
      fileInput.value = "";
      fileName.hidden = true;
      fileName.textContent = "";
      setError(null);
      selectType("bug");
      formCard.hidden = false;
      confirmCard.hidden = true;
      titleInput.focus();
    }

    typeBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        const t = btn.dataset["type"];
        if (t === "bug" || t === "feature" || t === "general") selectType(t);
      });
    });

    fileInput.addEventListener("change", () => {
      const f = fileInput.files?.[0];
      if (f) {
        fileName.hidden = false;
        fileName.textContent = `${f.name} · ${(f.size / 1024).toFixed(0)} KB`;
      } else {
        fileName.hidden = true;
        fileName.textContent = "";
      }
    });

    resetBtn.addEventListener("click", resetForm);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      if (submitting) return;
      setError(null);

      const title = titleInput.value.trim();
      const desc  = descInput.value.trim();
      if (!title) { setError("Add a short title before submitting."); titleInput.focus(); return; }
      if (!desc)  { setError("Add a description so we can act on this."); descInput.focus(); return; }

      submitting = true;
      submitBtn.disabled = true;
      submitLbl.textContent = "Submitting…";

      const fd = new FormData();
      fd.append("portal", "retailer");
      fd.append("type", selectedType);
      fd.append("title", title);
      fd.append("description", desc);
      // Capture submitter identity so admin sees WHO sent it + can reply.
      // retailerId ensures the row's retailer_id (→ business name) is set even
      // though this form posts via plain fetch (no x-retailer-id header).
      if (ctx.user?.retailerId) fd.append("retailerId", ctx.user.retailerId);
      const submitterName = ctx.user?.firstName || ctx.user?.retailerName || "";
      if (submitterName) fd.append("submitterName", submitterName);
      const file = fileInput.files?.[0];
      if (file) fd.append("screenshot", file);

      void (async () => {
        try {
          const res = await fetch("/api/portal/feedback", {
            method: "POST",
            credentials: "include",
            body: fd,
          });
          const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
          if (!res.ok || !data.ok) {
            throw new Error(data.error || `HTTP ${res.status}`);
          }
          showConfirm();
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Submission failed";
          setError(`Could not submit feedback: ${msg}`);
        } finally {
          submitting = false;
          submitBtn.disabled = false;
          submitLbl.textContent = "Submit feedback";
        }
      })();
    });

    // Best-effort: focus the title field on mount.
    window.setTimeout(() => titleInput.focus(), 60);

    void ctx; // ctx unused here; kept for interface consistency

    return () => {
      container.classList.remove("fb-page-root");
      container.innerHTML = "";
    };
  },
};

registerPage(feedbackPage);
